import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useSettings } from "@/contexts/SettingsContext";
import { setMetaDescription, setCanonicalUrl, setOpenGraphTags, setTwitterCardTags, setMetaRobots } from "@/lib/seo";
import { generateWebPageSchema, generateBreadcrumbListSchema, addSchemaToPage } from "@/lib/schema";

export default function SettingsPage() {
  useEffect(() => {
    const title = "Settings - Munthly";
    const description = "Customise your Munthly budgeting preferences. Set your currency symbol and date format to personalise your free budgeting experience.";
    
    document.title = title;
    setMetaDescription(description);
    const canonicalCleanup = setCanonicalUrl("/settings");
    const ogCleanup = setOpenGraphTags(title, description, "/settings");
    const twitterCleanup = setTwitterCardTags(title, description);
    const robotsCleanup = setMetaRobots("noindex, nofollow"); // Protected page - don't index
    
    // Add schema for settings page
    const webPageSchema = generateWebPageSchema(
      "/settings",
      title,
      description
    );
    const breadcrumbSchema = generateBreadcrumbListSchema([
      { name: "Home", url: "/" },
      { name: "Dashboard", url: "/dashboard" },
      { name: "Settings", url: "/settings" }
    ]);
    
    const schemaCleanup = addSchemaToPage([webPageSchema, breadcrumbSchema]);
    
    return () => {
      canonicalCleanup();
      ogCleanup();
      twitterCleanup();
      robotsCleanup();
      schemaCleanup();
    };
  }, []);

  const { settings, updateSettings } = useSettings();
  const [currencySymbol, setCurrencySymbol] = useState(settings.currencySymbol);
  const [dateFormat, setDateFormat] = useState(settings.dateFormat);
  const [saved, setSaved] = useState(false);

  const handleSave = () => {
    updateSettings({ currencySymbol, dateFormat });
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const currencyOptions = [
    { value: "$", label: "$ - US Dollar" },
    { value: "£", label: "£ - British Pound" },
    { value: "€", label: "€ - Euro" },
    { value: "¥", label: "¥ - Japanese Yen" },
    { value: "₹", label: "₹ - Indian Rupee" },
    { value: "R", label: "R - South African Rand" },
  ];

  const dateFormatOptions = [
    { value: "MM/DD/YYYY", label: "MM/DD/YYYY (12/28/2025)" },
    { value: "DD/MM/YYYY", label: "DD/MM/YYYY (28/12/2025)" },
    { value: "YYYY-MM-DD", label: "YYYY-MM-DD (2025-12-28)" },
  ];

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="page-heading text-2xl sm:text-3xl font-semibold tracking-tight">Settings</h1>
        <p className="text-sm text-muted-foreground mt-1">Customize your preferences</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Display Preferences</CardTitle>
          <CardDescription>Customise how currency and dates are displayed</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="currency">Currency Symbol</Label>
            <Select value={currencySymbol} onValueChange={setCurrencySymbol}>
              <SelectTrigger id="currency" className="h-12 rounded-2xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {currencyOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="dateFormat">Date Format</Label>
            <Select value={dateFormat} onValueChange={setDateFormat}>
              <SelectTrigger id="dateFormat" className="h-12 rounded-2xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {dateFormatOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-4 pt-2">
            <Button onClick={handleSave}>
              Save Changes
            </Button>
            {saved && (
              <span className="text-sm font-medium" style={{ color: 'var(--success)' }}>✓ Settings saved</span>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
